/*!
 * scales.js — degree: in a scale:, from a root:
 * © 2026 Rob Canning — GPLv3
 *
 *     synth(degree:Prand(0, 2, 4, 7, inf), scale:minor, root:~A3, dur:0.25)
 *     synth(degree:Pseq([0, 2, 4], 3, inf), scale:dorian)     chords: [0, 2, 4]
 *     synth(degree:Pseq(0, -1, -3, inf), scale:[0, 3, 5, 7, 10])   any steps
 *
 * As in SuperCollider (Pbind's \degree, \scale, \root): degree 0 is the
 * root, 1 the next step of the scale, and past the last step the scale goes
 * on an octave up (7 in a seven-note scale is the root an octave higher, -1
 * the step below the root). A fraction lies between two steps, a microtone.
 *
 * The degrees are turned into MIDI notes (m57, m60.5) before synth() reads
 * them, so everything freq: does -- patterns, chords, rests, glide -- works
 * on them unchanged. A rand() inside a pattern is turned when it is drawn
 * (pitchToHz, synth.js), on the same scale.
 */

export const SCALES = {
  major:         [0, 2, 4, 5, 7, 9, 11],
  ionian:        [0, 2, 4, 5, 7, 9, 11],
  minor:         [0, 2, 3, 5, 7, 8, 10],
  aeolian:       [0, 2, 3, 5, 7, 8, 10],
  dorian:        [0, 2, 3, 5, 7, 9, 10],
  phrygian:      [0, 1, 3, 5, 7, 8, 10],
  lydian:        [0, 2, 4, 6, 7, 9, 11],
  mixolydian:    [0, 2, 4, 5, 7, 9, 10],
  locrian:       [0, 1, 3, 5, 6, 8, 10],
  harmonicminor: [0, 2, 3, 5, 7, 8, 11],
  melodicminor:  [0, 2, 3, 5, 7, 9, 11],
  pentatonic:    [0, 2, 4, 7, 9],
  majpent:       [0, 2, 4, 7, 9],
  minpent:       [0, 3, 5, 7, 10],
  blues:         [0, 3, 5, 6, 7, 10],
  whole:         [0, 2, 4, 6, 8, 10],
  chromatic:     [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
  hirajoshi:     [0, 2, 3, 7, 8],
};

const NOTE = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

/** A scale: a name (any case, minor / harmonicMinor) or a list of semitones. */
export function scaleSteps(scale) {
  if (Array.isArray(scale)) {
    const s = scale.map(Number).filter(Number.isFinite);
    return s.length ? s : SCALES.major;
  }
  return SCALES[String(scale ?? "major").toLowerCase().replace(/[^a-z]/g, "")] ?? SCALES.major;
}

/** root: as a MIDI note -- ~A3, A3, m57, a MIDI number, or Hz (more than 127). Default middle C. */
export function rootMidi(root) {
  if (root == null || root === "") return 60;
  if (typeof root === "number") return root > 127 ? 69 + 12 * Math.log2(root / 440) : root;
  const s = String(root).trim();
  const midi = s.match(/^~?m(-?\d+(?:\.\d+)?)$/i);
  if (midi) return Number(midi[1]);
  const n = s.match(/^~?([A-Ga-g])([#b]?)(-?\d+)?$/);
  if (n) {
    const pc = NOTE[n[1].toUpperCase()] + (n[2] === "#" ? 1 : n[2] === "b" ? -1 : 0);
    return 12 * (Number(n[3] ?? 4) + 1) + pc;
  }
  const num = Number(s);
  if (Number.isFinite(num)) return rootMidi(num);
  return 60;
}

/** The MIDI note of a degree in a scale (steps) above a root (MIDI). */
export function degreeToMidi(degree, steps, root = 60) {
  const d = Number(degree);
  if (!Number.isFinite(d)) return null;
  const n = steps.length;
  const lo = Math.floor(d);
  const at = k => {
    const oct = Math.floor(k / n);
    return steps[k - oct * n] + 12 * oct;
  };
  const frac = d - lo;
  const semis = frac ? at(lo) + (at(lo + 1) - at(lo)) * frac : at(lo);
  return root + semis;
}

const isRest = v => typeof v === "string" && /^~?r(est)?$/i.test(v.trim());
const fmt = m => `m${+m.toFixed(4)}`;

/**
 * degree: as freq: would be written -- every number in it (in lists,
 * chords, patterns, nested patterns) a MIDI note. Rests stay rests; a
 * rand() inside a pattern becomes a degree() call that synth() draws later.
 */
export function degreesToPitches(value, scale, root) {
  const steps = scaleSteps(scale);
  const r = rootMidi(root);
  const walk = v => {
    if (v == null || isRest(v)) return v;
    if (Array.isArray(v)) return v.map(walk);
    if (typeof v === "object") {
      if (v.type === "pattern") {
        // Pwhite(lo, hi[, n]): a range, drawn as numbers -- here a rand(lo, hi)
        // each step, turned into a degree when drawn. (The parser reads
        // Pwhite(0, 7) as values [0], repeats 7: 7 is then the top.)
        if (/^P(white|exprand)$/.test(v.name)) {
          const nums = (v.values ?? []).map(Number).filter(Number.isFinite);
          const fin = v.repeats != null && v.repeats !== "inf" && Number.isFinite(Number(v.repeats));
          const [lo, hi, n] = nums.length >= 2 ? [nums[0], nums[1], v.repeats]
            : [nums[0] ?? 0, fin ? Number(v.repeats) : nums[0] ?? 0, null];
          return { type: "pattern", name: "Pseq", repeats: n ?? null,
            values: [walk({ type: "funcCall", name: "rand", args: [lo, hi] })] };
        }
        return { ...v, values: (v.values ?? v.args ?? []).map(walk) };
      }
      if (v.type === "funcCall" || v.type === "rand" || v.type === "irand") {
        return { type: "funcCall", name: "degree", args: [v], steps, root: r };
      }
      return v;                            // a follow(): Hz, as written
    }
    const m = degreeToMidi(v, steps, r);
    return m == null ? v : fmt(m);
  };
  return walk(value);
}
